// src/component/AdminPrefectureCityMismatch.js
//
// 県と市区町村がチグハグなユーザーの洗い出し（/admin/prefecture-city-mismatch）
//
// 検出条件：
//   - users/{uid}.prefecture が日本の都道府県
//   - users/{uid}.city が空でない
//   - その city が public/data/japanCities.json[prefecture] のリストに含まれない
//
// 例：prefecture="北海道" だが city="渋谷区"
//
// users コレクションは約5万件あるためページングで全件スキャン。
// 結果は adminAggregates/prefectureCityMismatch に保存し、再訪時は即時表示。

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  CircularProgress,
  LinearProgress,
  Avatar,
  IconButton,
  Chip,
  TextField,
  Alert,
  Divider,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import SearchIcon from "@mui/icons-material/Search";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";

import { db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";
import { is4kiPaidUser } from "./groupActivity2026Audience";

const AGG_REF = doc(db, "adminAggregates", "prefectureCityMismatch");

function cityIncluded(cityList, city) {
  if (!Array.isArray(cityList)) return false;
  return cityList.some((c) =>
    typeof c === "string" ? c === city : c?.name === city
  );
}

function isMismatch(user, citiesByPref) {
  const pref = (user.prefecture || "").trim();
  const city = (user.city || "").trim();
  if (!pref || !city) return false;
  if (pref.startsWith("海外")) return false;
  const list = citiesByPref[pref];
  if (!list) return false;
  return !cityIncluded(list, city);
}

function formatDate(ts) {
  if (!ts) return "";
  try {
    const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
    return d.toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function AdminPrefectureCityMismatch() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const appBg = theme.appBg || "#F5F5F9";
  const textBase = theme.railText || "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";

  const [citiesByPref, setCitiesByPref] = useState({});
  const [citiesLoaded, setCitiesLoaded] = useState(false);
  const [citiesError, setCitiesError] = useState("");

  const [mismatched, setMismatched] = useState([]);
  const [totalScanned, setTotalScanned] = useState(0);
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState("");

  const [scanning, setScanning] = useState(false);
  const [docsFetched, setDocsFetched] = useState(0);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [filterText, setFilterText] = useState("");

  const abortRef = useRef(false);

  // 市区町村マップを起動時に取得
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/data/japanCities.json", {
          cache: "force-cache",
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const json = await res.json();
        if (!cancelled) {
          setCitiesByPref(json || {});
          setCitiesLoaded(true);
        }
      } catch (e) {
        console.error("japanCities.json load error:", e);
        if (!cancelled) {
          setCitiesError("市区町村マスタの読込に失敗しました。");
          setCitiesLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 保存済み集計を起動時に取得
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(AGG_REF);
        if (snap.exists()) {
          const d = snap.data() || {};
          setMismatched(Array.isArray(d.users) ? d.users : []);
          setTotalScanned(d.totalScanned || 0);
          setLastUpdatedLabel(formatDate(d.updatedAt));
        }
      } catch (e) {
        console.error("aggregate load error:", e);
      }
    })();
  }, []);

  const filteredRows = useMemo(() => {
    const ft = filterText.trim();
    if (!ft) return mismatched;
    return mismatched.filter((u) => {
      return (
        (u.name || "").includes(ft) ||
        (u.uid || "").includes(ft) ||
        (u.prefecture || "").includes(ft) ||
        (u.city || "").includes(ft)
      );
    });
  }, [mismatched, filterText]);

  const stopScan = () => {
    abortRef.current = true;
  };

  const runScan = async () => {
    if (!citiesLoaded || citiesError) {
      setError("市区町村マスタが利用できないためスキャンできません。");
      return;
    }
    setScanning(true);
    setError("");
    setInfo("");
    setDocsFetched(0);
    abortRef.current = false;

    const collected = [];
    let scanned = 0;

    try {
      // 2026参加（join + 4期支払い済み）に絞ってスキャン
      const q = query(
        collection(db, "users"),
        where("groupActivity2026.response", "==", "join")
      );
      const snap = await getDocs(q);

      snap.forEach((d) => {
        if (abortRef.current) return;
        const data = d.data() || {};
        if (!is4kiPaidUser(data)) return;
        scanned += 1;
        if (isMismatch(data, citiesByPref)) {
          collected.push({
            uid: d.id,
            email: data.email || "",
            name: data.name || "",
            avatar: data.avatar || "",
            prefecture: data.prefecture || "",
            city: data.city || "",
          });
        }
      });
      setDocsFetched(scanned);

      if (abortRef.current) {
        setInfo(
          `中断しました（${scanned} 件処理、ヒット ${collected.length} 件）。保存はしていません。`
        );
      } else {
        setMismatched(collected);
        setTotalScanned(scanned);
        await setDoc(AGG_REF, {
          users: collected,
          totalScanned: scanned,
          updatedAt: serverTimestamp(),
        });
        const now = new Date();
        setLastUpdatedLabel(
          now.toLocaleString("ja-JP", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        );
        setInfo(
          `スキャン完了：${scanned} 件中 ${collected.length} 件がチグハグでした。`
        );
      }
    } catch (e) {
      console.error("scan error:", e);
      setError(`スキャン中にエラーが発生しました：${e.message || e}`);
    } finally {
      setScanning(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100%", background: appBg, pb: 6 }}>
      <Box
        sx={{
          maxWidth: 1100,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 800, color: textBase }}>
            県・市区町村がチグハグなユーザー
          </Typography>
        </Stack>

        <Paper
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 2,
            border: `1px solid ${border}`,
            borderRadius: 2,
            bgcolor: surface,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: textBase, opacity: 0.85, lineHeight: 1.8, mb: 1.5 }}
          >
対象：2026グループ活動「参加する」かつ4期支払い済みユーザー。
検出条件：日本の都道府県を選択しているが、登録された市区町村名がその都道府県の市区町村リストに含まれない（例：北海道なのに「渋谷区」）。
          </Typography>

          {citiesError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {citiesError}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
          {info && (
            <Alert severity="success" sx={{ mb: 1.5 }}>
              {info}
            </Alert>
          )}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="contained"
              startIcon={
                scanning ? <CircularProgress size={16} /> : <RefreshRoundedIcon />
              }
              onClick={runScan}
              disabled={scanning || !citiesLoaded}
              sx={{
                bgcolor: accent,
                color: "#fff",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { bgcolor: accent, opacity: 0.92 },
              }}
            >
              {scanning ? "スキャン中…" : "全件スキャン"}
            </Button>
            {scanning && (
              <Button
                variant="outlined"
                onClick={stopScan}
                sx={{ textTransform: "none" }}
              >
                中断
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            {lastUpdatedLabel && (
              <Typography
                variant="caption"
                sx={{ color: textBase, opacity: 0.65 }}
              >
                最終スキャン：{lastUpdatedLabel}（{totalScanned} 件中 {mismatched.length} 件ヒット）
              </Typography>
            )}
          </Stack>

          {scanning && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: textBase, opacity: 0.7 }}>
                読み取り済み {docsFetched} 件 / ヒット {/* live count */}
              </Typography>
              <LinearProgress sx={{ mt: 0.5 }} />
            </Box>
          )}
        </Paper>

        <Paper
          sx={{
            border: `1px solid ${border}`,
            borderRadius: 2,
            bgcolor: surface,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderBottom: `1px solid ${border}`,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: textBase }}>
              ヒット一覧（{filteredRows.length} 件）
            </Typography>
            <Box sx={{ flex: 1 }} />
            <TextField
              size="small"
              placeholder="名前 / uid / 県 / 市で絞り込み"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{ fontSize: 18, mr: 0.5, color: "rgba(0,0,0,0.4)" }}
                  />
                ),
              }}
              sx={{ width: { xs: "100%", sm: 280 } }}
            />
          </Box>

          {filteredRows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: textBase, opacity: 0.65 }}>
                {mismatched.length === 0
                  ? "まだスキャンしていません。「全件スキャン」を実行してください。"
                  : "該当するユーザーはいません。"}
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider sx={{ borderColor: border }} />}>
              {filteredRows.map((u) => (
                <Stack
                  key={u.uid}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ px: 2, py: 1.25 }}
                >
                  <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <Avatar
                      src={u.avatar || undefined}
                      sx={{ width: 36, height: 36, bgcolor: `${accent}20` }}
                    >
                      {(u.name || "?").slice(0, 1)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: textBase }}
                        noWrap
                      >
                        {u.name || "(名前未設定)"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(0,0,0,0.5)",
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                          fontSize: 10,
                          display: "block",
                        }}
                        noWrap
                      >
                        {u.uid}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Chip
                      label={u.prefecture}
                      size="small"
                      sx={{
                        bgcolor: `${accent}12`,
                        color: accent,
                        fontWeight: 700,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(0,0,0,0.45)" }}
                    >
                      ×
                    </Typography>
                    <Chip
                      label={u.city}
                      size="small"
                      sx={{
                        bgcolor: "rgba(244,67,54,0.10)",
                        color: "#c62828",
                        fontWeight: 700,
                      }}
                    />
                  </Stack>

                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNewRoundedIcon />}
                    onClick={async () => {
                      try {
                        let email = u.email || "";
                        if (!email && u.uid) {
                          const snap = await getDoc(doc(db, "users", u.uid));
                          email = snap.exists() ? (snap.data()?.email || "") : "";
                        }
                        await navigator.clipboard?.writeText(email);
                      } catch (e) {
                        // ignore clipboard error
                      }
                      navigate("/admin/users");
                    }}
                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    メアドコピー＆ユーザー画面へ
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
